import React, { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import './styles/globals.css'
import { config } from './config'

export interface User {
  sub: string
  email: string
  username: string
  legacy_name?: string
  verified?: boolean
}

// Migration routes that bypass authentication
const MIGRATION_ROUTES = [
  '/link_account',
  '/link_success', 
  '/link_error'
]

// Check if current path is a migration route
function isMigrationRoute(pathname: string): boolean {
  return MIGRATION_ROUTES.includes(pathname)
}

// Authentication hook
function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    // Check if we have a valid session via API
    fetch(`${config.apiUrl}/auth/whoami`, {
      credentials: 'include' // Sends HttpOnly cookie automatically
    })
    .then(response => response.ok ? response.json() : null)
    .then(setUser)
    .finally(() => setLoading(false))
  }, [])
  
  const login = () => {
    // Redirect to API login endpoint - it handles Cognito
    window.location.href = `${config.apiUrl}/auth/login`
  }
  
  const logout = () => {
    // Let API handle proper session cleanup
    window.location.href = `${config.apiUrl}/auth/logout`
  }
  
  const apiCall = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    return fetch(`${config.apiUrl}${endpoint}`, {
      ...options,
      credentials: 'include', // Include session cookie
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    })
  }, [])
  
  return { user, isAuthenticated: !!user, loading, login, logout, apiCall }
}

// Import components
import { Layout } from './components/Layout'
import { HomePage } from './pages/HomePage'
import { ProfilePage } from './pages/ProfilePage'
import { GalaxyBrowserPage } from './pages/GalaxyBrowserPage'
import { LinkAccountPage } from './pages/migration/LinkAccountPage'
import { LinkSuccessPage } from './pages/migration/LinkSuccessPage'
import { LinkErrorPage } from './pages/migration/LinkErrorPage'

// Login Prompt component (for users who need to authenticate)
function LoginPrompt() {
  const { login } = useAuth()
  
  return (
    <div className="page">
      <div className="container">
        <div className="hero">
          <h1>Welcome to SWGRealms!</h1>
          <p className="hero-subtitle">
            Please sign in to continue your galactic adventures.
          </p>
          
          <div className="hero-actions">
            <button 
              className="btn btn-primary"
              onClick={login}
              style={{ fontSize: '1.2rem', padding: 'var(--swg-spacing-md) var(--swg-spacing-2xl)' }}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Main App component
function App() {
  const { user, isAuthenticated, loading, logout } = useAuth()
  
  useEffect(() => {
    // Check if current path is a migration route
    const currentPath = window.location.pathname
    const isOnMigrationRoute = isMigrationRoute(currentPath)
    
    // If not authenticated and not loading, check if first-time user (but skip for migration routes)
    if (!loading && !isAuthenticated && !isOnMigrationRoute) {
      const deviceId = localStorage.getItem('swgrealms_device_id')
      
      if (!deviceId) {
        // First-time user - redirect to static site
        const bounceUrl = `${config.homeUrl}?play_not_loggedin=true`
        console.log('First-time user, redirecting to static site:', bounceUrl)
        window.location.href = bounceUrl
        return
      }
    }
  }, [loading, isAuthenticated])
  
  const handleSignOut = () => {
    // Clear device tracking and redirect via API logout
    localStorage.removeItem('swgrealms_device_id')
    localStorage.removeItem('swgrealms_last_user')
    logout()
  }
  
  if (loading) {
    return (
      <div className="page">
        <div className="container text-center">
          <h1>Loading...</h1>
        </div>
      </div>
    )
  }
  
  // Check if we're on a migration route
  const currentPath = window.location.pathname
  const isOnMigrationRoute = isMigrationRoute(currentPath)
  
  // If on migration route, render migration pages regardless of auth status
  if (isOnMigrationRoute) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/link_account" element={<LinkAccountPage />} />
          <Route path="/link_success" element={<LinkSuccessPage />} />
          <Route path="/link_error" element={<LinkErrorPage />} />
        </Routes>
      </BrowserRouter>
    )
  }
  
  if (!isAuthenticated) {
    return <LoginPrompt />
  }
  
  return (
    <BrowserRouter>
      <Layout user={user!} onSignOut={handleSignOut}>
        <Routes>
          <Route path="/" element={<HomePage user={user!} />} />
          <Route path="/dashboard" element={<HomePage user={user!} />} />
          <Route path="/profile" element={<ProfilePage user={user!} />} />
          <Route path="/galaxies" element={<GalaxyBrowserPage user={user!} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root')!)
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
